import { AuthenticationApi, ValidateFormatEnum } from "@/lib/api/api/authentication-api";
import { decodeToken, tileRequestToken } from "@/lib/token";
import { ConfigApi } from "@/lib/api/api/config-api";
import { CustomLayersApi } from "@/lib/api/api/custom-layers-api";
import { DataApi } from "@/lib/api/api/data-api";
import { EventQueryApi } from "@/lib/api/api/event-query-api";
import { LicencingApi } from "@/lib/api/api/licencing-api";
import { LoginApi } from "@/lib/api/api/login-api";
import { ManagementApi } from "@/lib/api/api/management-api";
import { MediaApi } from "@/lib/api/api/media-api";
import { NotificationApi } from "@/lib/api/api/notification-api";
import { OAuthApi } from "@/lib/api/api/oauth-api";
import { OrganisationApi } from "@/lib/api/api/organisation-api";
import { URLShortenerApi } from "@/lib/api/api/urlshortener-api";
import { UserApi } from "@/lib/api/api/user-api";
import { UsersApi } from "@/lib/api/api/users-api";
import { Configuration } from "@/lib/api/configuration";
import {
    BfClient,
    BfClientConfig,
    BfDevice,
    BfEventType,
    BfGroupName,
    BfHandlebarsTemplate,
    BfHandlebarsWhenTemplateApplies,
    BfInstance,
    BfMapLayer,
    BfMapUserPreference,
    BfNewsItem,
    BfOrg,
    BfPlanTypes,
    BfRegion,
    BfRegionName,
    BfSentryConfig,
    BfTokenClaims,
    BfUnitSystem,
    BfUser,
    DistanceUnits,
    SpeedUnits,
    TemperatureUnits,
} from "@/lib/api/models";
import {
    apiUrl,
    clientVersion,
    DEBUG_MODE,
    deleteStateFromLocalStorage,
    EXPECTED_EULA_VERSION,
    getCient,
    getStateFromLocalStorage,
    InstanceRendering,
    isiOSApp,
    isSpecialInstance,
    isWebKit,
    LocalStorageClientSettings,
    MAP_PREFERENCE_KEYS,
    saveStateToLocalStorage,
    V3_STATE_STORAGE_KEY,
} from "@/lib/client";
import { ForegroundMessaging } from "@/lib/messaging/foreground";
import * as Sentry from "@sentry/browser";
import Bowser from "bowser";
import { LngLat } from "mapbox-gl";
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { zaraz } from "zaraz-ts";

interface TimeMachineConfig {
    time: Date;
    layerConfig: BfMapLayer[];
}

export interface IAppContext {
    clientContactedApiEver: boolean | null;
    online: boolean;
    user?: BfUser;
    isLoggedIn: boolean;
    sentryConfig?: BfSentryConfig;
    unitSystem: BfUnitSystem;
    notificationsShouldBeActive: boolean;
    eulaAccepted: boolean;
    token?: string;
    /// The credential map requests carry to tiley; never a compact session token.
    tileToken?: string;
    configFromApi: BfClientConfig | null;
    title: string;
    copyright: string;
    org?: BfOrg;
    getMapCenter: () => LngLat | undefined;

    timemachineActive: TimeMachineConfig | null;
    setTimeMachineActive: (config: TimeMachineConfig | null) => void;

    currentDevice: () => BfDevice;
    login: (token: string) => Promise<void>;
    logout: (contactApi?: boolean) => Promise<void>;
    getConfigFromApi: () => Promise<BfClientConfig>;
    isInGroup: (groupName: BfGroupName | BfGroupName[]) => boolean;
    isInGroupDelegate: (groupName: BfGroupName | BfGroupName[], delegateUser: BfUser) => boolean;
    isRoot: () => boolean;
    isDeveloper: () => boolean;
    isEnterprise: () => boolean;
    isManagement: () => boolean;
    isOrgManager: () => boolean;
    isModerator: () => boolean;
    highestPlan: () => BfPlanTypes;
    setEulaAccepted: () => void;
    unseenNews: BfNewsItem[];
    setNewsSeen: (ids: string[]) => void;
    decodedToken: (token?: string | null) => BfTokenClaims | null;
    definedRegions: () => BfRegion[];
    setNotificationsShouldBeActive: (active: boolean) => void;
    smsValidationRegions: () => string;
    updateRegion: (possibleNewRegions: BfRegionName[]) => boolean;
    region: BfRegionName[];
    bestRegionAvailable: () => BfRegionName;
    setMapCenter: (geometry: LngLat | undefined) => void;
    instanceRendering: InstanceRendering;
    syncLocalNotifications: () => void;
    getTemplateForLayerGroup: (
        eventType: BfEventType,
        unitSystem?: BfUnitSystem
    ) => BfHandlebarsTemplate | undefined;

    notificationApi: NotificationApi;
    userApi: UserApi;
    authApi: AuthenticationApi;
    eventQueryApi: EventQueryApi;
    licencingApi: LicencingApi;
    configApi: ConfigApi;
    managementApi: ManagementApi;
    customLayersApi: CustomLayersApi;
    usersApi: UsersApi;
    urlShortenerApi: URLShortenerApi;
    dataApi: DataApi;
    loginApi: LoginApi;
    orgApi: OrganisationApi;
    mediaApi: MediaApi;
    /// Stable until the session token changes.
    oauthApi: OAuthApi;
}

interface IAppContextProviderProps {
    children: ReactNode;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AppContext = createContext<IAppContext | undefined>(undefined);

export const AppContextProvider = ({ children }: IAppContextProviderProps) => {
    const initialState = getStateFromLocalStorage<LocalStorageClientSettings>(
        V3_STATE_STORAGE_KEY
    ) || {
        _clientContactedAPIEver: null,
        _clientToken: null,
        _clientTileToken: null,
        _clientDeviceId: uuidv4(),
        _clientMostRecentEULAAccepted: null,
        _clientNewsSeenIds: null,
        _configFromApi: null,
    };
    const [clientSettings, setClientSettings] = useState<LocalStorageClientSettings>(initialState);
    const [notificationsShouldBeActive, setNotificationsShouldBeActive] = useState<boolean>(false);
    const [online, setOnline] = useState<boolean>(navigator.onLine);
    const [region, setRegion] = useState<BfRegionName[]>([BfRegionName.global]);
    const [timemachineActive, setTimeMachineActive] = useState<TimeMachineConfig | null>(null);
    const [instance, setInstance] = useState<BfInstance>(BfInstance.uxv3);
    const mapCenterRef = useRef<LngLat | undefined>(undefined);

    const currentDevice = useCallback((): BfDevice => {
        const bowser = Bowser.getParser(window.navigator.userAgent);

        const device: BfDevice = {
            deviceId: clientSettings._clientDeviceId,
            os: bowser.getOSName(),
            osversion: bowser.getOSVersion(),
            useragent: window.navigator.userAgent,
            platform: bowser.getPlatformType(),
            client: getCient(),
            instance,
            build: isSpecialInstance(),
            resolution: `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio}`,
            iswebkit: isWebKit(),
            isiosapp: isiOSApp(),
            clientVersion: clientVersion(),
        };
        return device;
    }, [clientSettings._clientDeviceId, instance]);

    const apiConfig = useCallback(
        (token?: string) => {
            return new Configuration({
                basePath: apiUrl(),
                accessToken: token !== undefined ? token : clientSettings._clientToken || "",
            });
        },
        [clientSettings._clientToken]
    );

    const oauthApi = useMemo(() => new OAuthApi(apiConfig()), [apiConfig]);

    const save = useCallback((clientSettings: LocalStorageClientSettings) => {
        if (!clientSettings) {
            return;
        }
        saveStateToLocalStorage<LocalStorageClientSettings>(V3_STATE_STORAGE_KEY, clientSettings);

        if (DEBUG_MODE) console.log("AppContextProvider::save");
    }, []);

    const smsValidationRegions = () => {
        if (clientSettings._configFromApi && clientSettings._configFromApi.smsValidationRegions)
            return clientSettings._configFromApi.smsValidationRegions;
        else return "AU";
    };

    const definedRegions = () => {
        if (clientSettings._configFromApi && clientSettings._configFromApi.regions) {
            return clientSettings._configFromApi.regions;
        }

        const defaultRegion: BfRegion = {
            name: BfRegionName.global,
            friendly: "World",
            showInNav: false,
        };

        return [defaultRegion];
    };

    const setMapCenter = useCallback((geometry: LngLat | undefined) => {
        mapCenterRef.current = geometry; // no re-render on map move
    }, []);

    const getMapCenter = useCallback(() => mapCenterRef.current, []);

    const updateRegion = useCallback((possibleNewRegions: BfRegionName[]) => {
        // Defensive checks
        if (!possibleNewRegions?.length) return false;

        // Use functional form so we always get the **current** region
        let changed = false;
        setRegion((prevRegion) => {
            // If the region is the same, don't do anything
            if (possibleNewRegions[0] === prevRegion[0]) {
                return prevRegion;
            }

            // If the region is not the same, change it
            changed = true;
            console.log(`region changed from: ${prevRegion}, to: ${possibleNewRegions}`);
            return possibleNewRegions;

            // // Now we can do any logic with the old region safely
            // const regionStillVisible = possibleNewRegions
            //     .filter((r) => r !== BfRegionName.global)
            //     .some((regionName) => regionName === prevRegion.find((rn) => rn === regionName));

            // // If region is actually changing:
            // if (!regionStillVisible && possibleNewRegions[0] !== prevRegion[0]) {
            //     console.log(`region changed from: ${prevRegion}, to: ${possibleNewRegions}`);
            //     changed = true;
            //     return possibleNewRegions;
            // } else {
            //     return prevRegion; // No change
            // }
        });
        return changed; // or do something else to signal the caller
    }, []);

    const bestRegionAvailable = (): BfRegionName => {
        const bestRegion = region.find((r) => r !== BfRegionName.global);
        return bestRegion || BfRegionName.global;
    };

    const decodedToken = (token?: string | null): BfTokenClaims | null => decodeToken(token);

    const getGroups = useCallback(() => {
        const decoded = decodedToken(clientSettings._clientToken);
        if (!decoded) return null;
        return decoded.group;
    }, [clientSettings._clientToken]);

    const isInGroup = useCallback(
        (groupName: BfGroupName | BfGroupName[]): boolean => {
            const groups = getGroups();

            // Check if groupName is array
            if (Array.isArray(groupName)) {
                for (let i = 0; i < groupName.length; i++) {
                    if (groups && groups.includes(groupName[i])) return true;
                }
                return false;
            }

            // Check if user in group
            if (groups && groups.includes(groupName)) return true;

            return false;
        },
        [getGroups]
    );

    const isRoot = () => {
        const groups = getGroups();
        if (groups && groups.includes(BfGroupName.root)) return true;
        return false;
    };

    const isDeveloper = () => {
        return isInGroup(BfGroupName.developer);
    };

    const isManagement = () => {
        return isInGroup(BfGroupName.management) || isRoot();
    };

    const isModerator = () => {
        return isInGroup(BfGroupName.moderator) || isRoot();
    };

    const isEnterprise = () => {
        return isInGroup(BfGroupName.enterprise);
    };

    const isOrgManager = () => {
        const org = clientSettings._configFromApi?.org;
        const user = clientSettings._configFromApi?.user;
        if (!user || !org) return false;
        return (org && org.managerUserIds && org.managerUserIds.includes(user.userId)) || false;
    };

    const highestPlan = (): BfPlanTypes => {
        if (isInGroup([BfGroupName.business])) {
            return BfPlanTypes.business;
        } else if (isInGroup([BfGroupName.pro])) {
            return BfPlanTypes.pro;
        } else {
            return BfPlanTypes.free;
        }
    };

    const isInGroupDelegate = (
        groupName: BfGroupName | BfGroupName[],
        delegateUser: BfUser
    ): boolean => {
        const groups = delegateUser.groups?.map((g) => g.group);

        // Check if user is root
        // if (Authentication.isRoot()) return true;

        // Check if groupName is array
        if (Array.isArray(groupName)) {
            for (let i = 0; i < groupName.length; i++) {
                if (groups && groups.includes(groupName[i])) return true;
            }
            return false;
        }

        // Check if user in group
        if (groups && groups.includes(groupName)) return true;

        return false;
    };

    const unitSystem = useCallback((): BfUnitSystem => {
        let unitSystem = clientSettings._configFromApi?.unitSystem;

        if (!unitSystem) {
            unitSystem = {
                distance: DistanceUnits.kilometers,
                rainRate: DistanceUnits.millimeters,
                speed: SpeedUnits.kmph,
                temperature: TemperatureUnits.celsius,
                windbarbSpeed: SpeedUnits.kmph,
            };
        }

        return unitSystem;
    }, [clientSettings._configFromApi?.unitSystem]);

    const setEulaAccepted = () => {
        setClientSettings((existing) => {
            return {
                ...existing,
                _clientMostRecentEULAAccepted: EXPECTED_EULA_VERSION,
            };
        });
    };

    const setNewsSeen = (ids: string[]) => {
        setClientSettings((existing) => {
            return {
                ...existing,
                _clientNewsSeenIds: [...new Set([...(existing._clientNewsSeenIds ?? []), ...ids])],
            };
        });
    };

    const getConfigFromApi = useCallback(
        (config?: Configuration, token?: string): Promise<BfClientConfig> => {
            if (DEBUG_MODE) console.log("AppContextProvider::getConfigFromApi");

            const client: BfClient = { device: currentDevice() };
            const timeout = clientSettings._clientContactedAPIEver ? 5000 : undefined;
            const apiConfigUsed = config ? config : apiConfig();

            return new ConfigApi(apiConfigUsed)
                .clientConfig(client, {
                    timeout,
                })
                .then((response) => {
                    const data = response.data;

                    // Save client settings
                    setClientSettings((existing) => {
                        const clientToken = token !== undefined ? token : existing._clientToken;
                        const tileToken =
                            token !== undefined ? null : (existing._clientTileToken ?? null);

                        // A fresh device's first config marks current news seen (no backlog);
                        // existing devices keep null so current items show once.
                        const newsSeenIds =
                            existing._clientNewsSeenIds === null &&
                            !existing._clientContactedAPIEver
                                ? (data.news ?? []).map((item) => item.newsId)
                                : existing._clientNewsSeenIds;

                        return {
                            ...existing,
                            _clientContactedAPIEver: true,
                            _configFromApi: data,
                            _clientToken: clientToken,
                            _clientTileToken: tileToken,
                            _clientNewsSeenIds: newsSeenIds,
                        };
                    });

                    // Save user information in Sentry
                    const user = data.user || null;
                    if (user) {
                        Sentry.setUser({
                            id: user.userId,
                            email: user.email,
                            deviceId: clientSettings._clientDeviceId,
                        });
                    } else {
                        Sentry.setUser({ deviceId: clientSettings._clientDeviceId });
                    }

                    return data;
                })
                .catch((e) => {
                    console.warn(
                        "Error during configuration fetch, falling back to embedded config",
                        e
                    );

                    setClientSettings((existing) => {
                        if (!existing._clientContactedAPIEver) {
                            return {
                                ...existing,
                                _configFromApi: import.meta.env.FALLBACK_CONFIG,
                            };
                        }
                        return existing;
                    });

                    return import.meta.env.FALLBACK_CONFIG;
                });
        },
        [
            apiConfig,
            clientSettings._clientContactedAPIEver,
            clientSettings._clientDeviceId,
            currentDevice,
        ]
    );

    const login = useCallback(
        async (token: string): Promise<void> => {
            const decoded = decodedToken(token);
            if (!decoded) throw new Error("unable to decode token");

            return getConfigFromApi(apiConfig(token), token).then(() => {
                zaraz.track("login");
            });
        },
        [apiConfig, getConfigFromApi]
    );

    const logout = useCallback(
        (contactApi: boolean = true) => {
            // Await for de-registration but ignore result
            if (contactApi && clientSettings._clientDeviceId) {
                new NotificationApi(apiConfig())
                    .removeDevice(clientSettings._clientDeviceId)
                    .catch((e) => {
                        console.warn("error logging out", e);
                    });
            }

            // Remove all map preferences
            for (const pref of Object.values(MAP_PREFERENCE_KEYS)) {
                deleteStateFromLocalStorage(pref);
            }

            return getConfigFromApi(apiConfig(""), "").then(() => {
                zaraz.track("logout");
            });
        },
        [apiConfig, clientSettings._clientDeviceId, getConfigFromApi]
    );

    const validateToken = useCallback(
        (config?: Configuration, token?: string) => {
            const authApi = new AuthenticationApi(config ? config : apiConfig(token));

            const headers = clientSettings._clientDeviceId
                ? { "Bf-Device-Id": clientSettings._clientDeviceId }
                : undefined;

            authApi.validate(ValidateFormatEnum.compact, { headers }).catch((e) => {
                // If a http token 409 conflict is returned, then the current token is invalid, but the response contains the new token to use.
                if (e.response && e.response.status === 409) {
                    console.warn("Token is invalid, updating token");
                    setClientSettings((existing) => {
                        return {
                            ...existing,
                            _clientToken: e.response.data.token,
                            _clientTileToken: e.response.data.tileToken ?? null,
                        };
                    });
                } else if (e.response && e.response.status === 401) {
                    // Only
                    console.error("Token validation failed", e);
                    logout(false).catch((logoutError) => {
                        console.warn(
                            "Error during logout after token validation failure",
                            logoutError
                        );
                    });
                }
            });
        },
        [apiConfig, clientSettings._clientDeviceId, logout]
    );

    const instanceRendering = useCallback((): InstanceRendering => {
        switch (instance) {
            case BfInstance.battleground: {
                return {
                    showMap: true,
                    showNotifications: true,
                    showNotificationsInbox: false,
                    showNotificationsSettings: true,
                    showNotificationsCreate: false,
                    showFeedback: false,
                    showPlans: false,
                    showOrganisation: true,
                    showLogin: false,
                    showAccount: true,
                    showMore: true,
                    showMoreDiagnostics: false,
                    showMoreFaq: true,
                    showMoreSupportedDevices: true,
                    showMoreWelcome: false,
                    showMoreWorkWithUs: false,
                    showMoreAbout: false,
                    showMoreSystemStatus: true,
                    showMorePrivacyPolicy: false,
                    showMoreTermsOfService: false,
                    showMoreSocialLinks: false,
                    showMedia: false,
                    showWhatsNew: false,
                };
            }
            default:
                return new InstanceRendering();
        }
    }, [instance]);

    const updateStoredMapPreferences = useCallback((updatedPreferences: BfMapUserPreference) => {
        if (DEBUG_MODE) console.log("AppContextProvider::updateMapPreferences");

        setClientSettings((existing) => {
            if (
                existing._configFromApi &&
                existing._configFromApi.user &&
                existing._configFromApi.user.mapPreferences
            ) {
                const updatedMapPreferences = existing._configFromApi.user.mapPreferences.map(
                    (pref) => (pref.mapId === updatedPreferences.mapId ? updatedPreferences : pref)
                );

                return {
                    ...existing,
                    _configFromApi: {
                        ...existing._configFromApi,
                        user: {
                            ...existing._configFromApi.user,
                            mapPreferences: updatedMapPreferences,
                        },
                    },
                };
            }

            return existing;
        });
    }, []);

    const getTemplateForLayerGroup = (
        eventType: BfEventType,
        unitSystem: BfUnitSystem | undefined
    ): BfHandlebarsTemplate | undefined => {
        const templates = clientSettings._configFromApi?.templates
            ?.filter(
                (mt) =>
                    "eventType" in mt.applies.target && mt.applies.target.eventType === eventType
            ) // Find any template matching by layer group
            .sort((a, b) => {
                // Sort by the most specific unit system match
                if (!unitSystem) return -1;

                const calculateScore = (templateApplies: BfHandlebarsWhenTemplateApplies) => {
                    let score = 0;
                    if (templateApplies.units) {
                        score += templateApplies.units.distance === unitSystem.distance ? 1 : 0;
                        score += templateApplies.units.speed === unitSystem.speed ? 1 : 0;
                        score +=
                            templateApplies.units.temperature === unitSystem.temperature ? 1 : 0;
                        score += templateApplies.units.rainRate === unitSystem.rainRate ? 1 : 0;
                        score +=
                            templateApplies.units.windbarbSpeed === unitSystem.windbarbSpeed
                                ? 1
                                : 0;
                    }
                    return score;
                };

                return calculateScore(b.applies) - calculateScore(a.applies);
            });

        return templates?.[0];
    };

    const syncLocalNotifications = useCallback(() => {
        const user = clientSettings._configFromApi?.user;
        const notificationApi = new NotificationApi(apiConfig());

        if (!user || !user.userId) {
            return;
        }

        notificationApi
            .getNotificationState(user?.userId)
            .then((state) => {
                const notifications = state.data.notificationRequests;

                if (notifications) {
                    ForegroundMessaging.updateChannels(notifications);
                }
            })
            .catch((err) => {
                console.warn!("Error getting notification state", err);
            });
    }, [apiConfig, clientSettings._configFromApi?.user]);

    // When the app is first initialised, try and load the state from storage.
    // If it's not there, then generate a new client device id.
    // Updating the client device ID or token will trigger a subsequent save and fetch from the API.
    useEffect(() => {
        if (DEBUG_MODE) console.log("AppContextProvider::useEffect mount");

        // If this fails, the user may retain a copy of the previous users's map state, which is ok-enough
        getConfigFromApi()
            .catch((e) => {
                console.warn("error logging out during refresh", e);
            })
            .finally(() => {
                // Validate token
                if (clientSettings._clientToken) {
                    return validateToken(apiConfig(), clientSettings._clientToken);
                }
            })
            .catch((e) => {
                console.error("Error during initial configuration fetch", e);
            });

        const handleOnline = () => setOnline(true);
        const handleOffline = () => setOnline(false);

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        // Manually check query string (avoiding react re-rendering)
        const searchParams = new URLSearchParams(window.location.search);
        const instanceParam = searchParams.get("instance");
        if (instanceParam) {
            const parsedInstance = Object.values(BfInstance).find(
                (instance) => instance === instanceParam
            );
            if (parsedInstance) {
                if (DEBUG_MODE)
                    console.log("AppContextProvider::useEffect instance change", parsedInstance);
                setInstance(parsedInstance);
            }
        }

        const battlegroundDomain = window.location.hostname.startsWith("battleground");
        if (battlegroundDomain) {
            if (DEBUG_MODE) console.log("AppContextProvider::useEffect battleground domain");
            setInstance(BfInstance.battleground);
        }

        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (DEBUG_MODE) console.log("AppContextProvider::useEffect clientSettings change");
        save(clientSettings);
    }, [save, clientSettings]);

    const unseenNews = (clientSettings._configFromApi?.news ?? []).filter(
        (item) => !(clientSettings._clientNewsSeenIds ?? []).includes(item.newsId)
    );

    const value = {
        clientContactedApiEver: clientSettings._clientContactedAPIEver,
        online,
        user: clientSettings._configFromApi?.user || undefined,
        org: clientSettings._configFromApi?.org || undefined,
        isLoggedIn: !!clientSettings._configFromApi?.user,
        unitSystem: unitSystem(),
        token: clientSettings._clientToken || undefined,
        tileToken: tileRequestToken(clientSettings._clientToken, clientSettings._clientTileToken),
        configFromApi: clientSettings._configFromApi,
        title: clientSettings._configFromApi?.title || "",
        copyright: clientSettings._configFromApi?.copyright || "",
        region,
        notificationsShouldBeActive,
        sentryConfig: clientSettings._configFromApi?.sentry || undefined,
        eulaAccepted: clientSettings._clientMostRecentEULAAccepted === EXPECTED_EULA_VERSION,
        timemachineActive,
        getMapCenter: getMapCenter,
        instanceRendering: instanceRendering(),
        setTimeMachineActive,
        getConfigFromApi,
        isInGroup,
        isRoot,
        isDeveloper,
        isEnterprise,
        isManagement,
        isModerator,
        isInGroupDelegate,
        isOrgManager,
        login,
        logout,
        currentDevice,
        decodedToken,
        setEulaAccepted,
        unseenNews,
        setNewsSeen,
        definedRegions,
        setNotificationsShouldBeActive,
        smsValidationRegions,
        updateRegion,
        bestRegionAvailable,
        updateStoredMapPreferences,
        setMapCenter,
        getTemplateForLayerGroup,
        highestPlan,
        syncLocalNotifications,

        notificationApi: new NotificationApi(apiConfig()),
        authApi: new AuthenticationApi(apiConfig()),
        userApi: new UserApi(apiConfig()),
        eventQueryApi: new EventQueryApi(apiConfig()),
        licencingApi: new LicencingApi(apiConfig()),
        usersApi: new UsersApi(apiConfig()),
        configApi: new ConfigApi(apiConfig()),
        managementApi: new ManagementApi(apiConfig()),
        customLayersApi: new CustomLayersApi(apiConfig()),
        urlShortenerApi: new URLShortenerApi(apiConfig()),
        dataApi: new DataApi(apiConfig()),
        loginApi: new LoginApi(apiConfig()),
        orgApi: new OrganisationApi(apiConfig()),
        mediaApi: new MediaApi(apiConfig()),
        oauthApi,
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("AppContext useAppContext must be used within a AppContextProvider");
    }
    return context;
};
